import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../../components";
import { baseAxios, Context } from "../../../utils/config";
import { useAuth } from "../../../contexts/auth";
import {
  ROLE_SYSTEM_ADMIN,
  ROLE_JDTECH_ADMIN,
} from "../../../utils/constants";
import { colors } from "../..";

const GROUP_BY_OPTIONS = [
  { value: "month", label: "By Month" },
  { value: "week", label: "By Week" },
  { value: "day", label: "By Day" },
];

const SOURCE_CARDS = [
  { key: "walkIn", label: "Walk-in" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "online", label: "Online" },
];

const MAX_RANGE_DAYS = 366;

function formatPercent(value) {
  if (value == null || Number.isNaN(Number(value))) return "0%";
  return `${Math.round(Number(value))}%`;
}

function formatDateToISO(date) {
  if (!date || !dayjs(date).isValid()) return "";
  return dayjs(date).format("YYYY-MM-DD");
}

function validateDateRange(startDate, endDate) {
  if (!startDate || !endDate || !dayjs(startDate).isValid() || !dayjs(endDate).isValid()) {
    toast.error("Please select both start and end dates", { duration: 3000 });
    return false;
  }
  if (dayjs(startDate).isAfter(dayjs(endDate), "day")) {
    toast.error("Start date must be before or equal to end date", {
      duration: 3000,
    });
    return false;
  }
  const daysDiff = dayjs(endDate).diff(dayjs(startDate), "day");
  if (daysDiff > MAX_RANGE_DAYS) {
    toast.error(`Date range cannot exceed ${MAX_RANGE_DAYS} days`, {
      duration: 3000,
    });
    return false;
  }
  return true;
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Booking Sources Report - Appointment booking source analysis
 */
const BookingSourcesReport = () => {
  const { location: globalLocation } = useContext(Context);
  const { user } = useAuth();

  const isOrgAdmin =
    user?.userRoles?.includes(ROLE_SYSTEM_ADMIN) ||
    user?.userRoles?.includes(ROLE_JDTECH_ADMIN);
  const selectedLocationId = globalLocation?.location_id ?? -1;
  const needsLocationSelection = isOrgAdmin && selectedLocationId === -1;

  const [startDate, setStartDate] = useState(dayjs().subtract(30, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [groupBy, setGroupBy] = useState("month");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("period");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    if (needsLocationSelection) {
      setRows([]);
      setSummary(null);
      return;
    }

    if (!validateDateRange(startDate, endDate)) {
      return;
    }

    setLoading(true);
    try {
      const response = await baseAxios.get("/booking-sources-report", {
        params: {
          startDate: formatDateToISO(startDate),
          endDate: formatDateToISO(endDate),
          groupBy,
        },
      });
      setSummary(response.data.summary ?? null);
      setRows(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      setRows([]);
      setSummary(null);
      toast.error(
        error.response?.data?.message || "Error loading report",
        { duration: 3000 }
      );
    } finally {
      setLoading(false);
    }
  }, [endDate, groupBy, needsLocationSelection, startDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [rows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }

    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Booking Sources");
      worksheet.columns = [
        { header: "Period", key: "period", width: 16 },
        { header: "Label", key: "periodLabel", width: 18 },
        { header: "Walk-in", key: "walkIn", width: 12 },
        { header: "Phone", key: "phone", width: 12 },
        { header: "Email", key: "email", width: 12 },
        { header: "Online", key: "online", width: 12 },
        { header: "Total", key: "total", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => {
        worksheet.addRow({
          period: row.period,
          periodLabel: row.periodLabel,
          walkIn: Number(row.walkIn) || 0,
          phone: Number(row.phone) || 0,
          email: Number(row.email) || 0,
          online: Number(row.online) || 0,
          total: Number(row.total) || 0,
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `BookingSources_${Date.now()}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "period",
        label: "Period",
        field: "period",
        minWidth: 130,
        render: (value) => value || "-",
      },
      {
        id: "periodLabel",
        label: "Label",
        field: "periodLabel",
        minWidth: 140,
        render: (value) => value || "-",
      },
      {
        id: "walkIn",
        label: "Walk-in",
        field: "walkIn",
        minWidth: 100,
        render: (value) => Number(value) || 0,
      },
      {
        id: "phone",
        label: "Phone",
        field: "phone",
        minWidth: 100,
        render: (value) => Number(value) || 0,
      },
      {
        id: "email",
        label: "Email",
        field: "email",
        minWidth: 100,
        render: (value) => Number(value) || 0,
      },
      {
        id: "online",
        label: "Online",
        field: "online",
        minWidth: 100,
        render: (value) => Number(value) || 0,
      },
      {
        id: "total",
        label: "Total",
        field: "total",
        minWidth: 100,
        render: (value) => (
          <Typography component="span" fontWeight={700} variant="body2">
            {Number(value) || 0}
          </Typography>
        ),
      },
    ],
    []
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ px: 8, py: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            mt: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
              Appointment Booking Sources
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Analyze how appointments were booked across walk-in, phone, email,
              and online.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(value) => setStartDate(value)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "background.paper",
                      borderRadius: "6px",
                    },
                  },
                },
              }}
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(value) => setEndDate(value)}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "background.paper",
                      borderRadius: "6px",
                    },
                  },
                },
              }}
            />
            <FormControl
              size="small"
              sx={{
                minWidth: 140,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "background.paper",
                  borderRadius: "6px",
                },
              }}
            >
              <InputLabel id="booking-sources-group-by">Group by</InputLabel>
              <Select
                labelId="booking-sources-group-by"
                label="Group by"
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
              >
                {GROUP_BY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              aria-label="Refresh report"
              onClick={loadReport}
              disabled={loading || needsLocationSelection}
              size="medium"
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                borderRadius: "6px",
                "&:hover": { bgcolor: "grey.100" },
              }}
            >
              <RefreshIcon />
            </IconButton>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={loading || needsLocationSelection || !sortedRows.length}
              sx={{
                minHeight: 40,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={loadReport}
              disabled={loading || needsLocationSelection}
              sx={{
                minHeight: 40,
                px: 2,
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {loading ? "Loading…" : "Run Report"}
            </Button>
          </Box>
        </Box>

        {needsLocationSelection && (
          <Alert
            severity="info"
            sx={{
              mb: 2,
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "primary.main",
            }}
          >
            Please select a location from the header to view this report.
          </Alert>
        )}

        {!needsLocationSelection && summary && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "repeat(4, 1fr)",
              },
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {SOURCE_CARDS.map(({ key, label }) => {
              const item = summary[key] || { count: 0, percentage: 0 };
              return (
                <Box
                  key={key}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: "6px",
                    bgcolor: "background.paper",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      px: 2,
                      py: 0.75,
                      bgcolor: "secondary.main",
                      borderBottom: "1px solid",
                      borderColor: "primary.main",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.gold,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography fontWeight={700} sx={{ fontSize: "1.75rem", lineHeight: 1.1 }}>
                      {Number(item.count) || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {formatPercent(item.percentage)} of total
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        {!needsLocationSelection && (
          <PaginatedTable
            columns={columns}
            rows={sortedRows}
            totalRowCount={sortedRows.length}
            loading={loading}
            onPaginationChange={noopPagination}
            onSortChange={handleSortChange}
            initialOrderBy="period"
            initialOrder="desc"
            searchQuery={`${formatDateToISO(startDate)}|${formatDateToISO(endDate)}|${groupBy}|${selectedLocationId}`}
            clientSidePagination
            skeletonHeight="40px"
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default BookingSourcesReport;
